import {lazy, Suspense, useMemo, useState} from 'react';
import {constructGalleryImageUrl, constructListingImageUrl} from '../../../util/ConstructImageUrlUtil';
import {Grid, Skeleton, useMediaQuery} from '@mui/material';
import './style.css'
import SecurSpaceModal from '../../common/SecurSpaceModal';
import {AiOutlineClose} from 'react-icons/ai'
import CarouselImage from '../../common/ImageCarousel/CarouselImage';
import {getLocationGallery} from '../../../util/LocationMediaUtils';

const ImageCarousel = lazy(() => import('../../common/ImageCarousel/ImageCarousel'));

interface LocationImageGalleryProps {
  locationId: string;
  listingImage?: string | null;
  gallery?: Array<string | null | undefined> | null;
}

const LocationImageGallery = ({ locationId, listingImage, gallery }: LocationImageGalleryProps) => {
  const items = useMemo(() => {
    const listingImageUrl = listingImage ? constructListingImageUrl(listingImage) : null;
    const galleryImagesUrls = getLocationGallery({gallery}).map((item) => constructGalleryImageUrl(`${locationId}/${item}`));
    return listingImageUrl ? [listingImageUrl, ...galleryImagesUrls] : galleryImagesUrls;
  }, [locationId, listingImage, gallery]);
  const galleryLength = items.length;
  const carouselLength = items.length > 0;
  const isSmallScreen = useMediaQuery('(max-width: 54.85em)');
  // Stacked layout per photo count (see .ss-gallery-layout-N): 2 → halves,
  // 3–4 → main photo plus two stacked, 5+ → main photo plus a 2×2. Photos
  // that don't fit are counted on the last tile and open in "See all photos".
  const galleryLayout = galleryLength >= 5 ? 5 : galleryLength >= 3 ? 3 : galleryLength === 2 ? 2 : 1;
  const stackedTileCount = {1: 0, 2: 1, 3: 2, 5: 4}[galleryLayout];
  const hiddenCount = galleryLength - 1 - stackedTileCount;
  const [openPhotoIndex, setOpenPhotoIndex] = useState<number | null>(null);
  const [inlinePhotoIndex, setInlinePhotoIndex] = useState(0);
  const isCarouselOpen = openPhotoIndex !== null;

  const openModal = (index = 0) => setOpenPhotoIndex(index);
  const closeModal = () => setOpenPhotoIndex(null);
  const mainPhoto = <CarouselImage
    fit="cover"
    className="ss-gallery-img"
    src={items[0]}
    alt={carouselLength ? 'Location photo 1' : 'No photos available'}
  />;

  return (
    <>
      {
        isSmallScreen &&
        <div className="ss-gallery-mobile">
          {items.length > 1 &&
            <button type="button" className="ss-absolute-bottom" onClick={() => openModal(inlinePhotoIndex)}>
              See All Photos
            </button>}
          {carouselLength ? <Suspense fallback={<Skeleton variant="rectangular" height="16em" />}>
            <ImageCarousel images={items} variant="inline" ariaLabel="Location photos" onImageClick={openModal} onSelect={setInlinePhotoIndex} />
          </Suspense> : <CarouselImage
            className="ss-gallery-img"
            src={items[0]}
            alt="No photos available"
          />}

        </div>
      }
      <Grid className={`ss-location-gallery-container ss-gallery-grid ss-gallery-layout-${galleryLayout}`}>
        <Grid className={`ss-gallery-tile ss-gallery-hero ${galleryLength === 0 ? "ss-gallery-empty" : ""}`}>
          {carouselLength ? <button
            type="button"
            className="ss-image-carousel-open"
            aria-label={`Open photo 1 of ${galleryLength}`}
            aria-haspopup="dialog"
            onClick={() => openModal(0)}
          >
            {mainPhoto}
          </button> : mainPhoto}

          {items.length > 1 &&
            <button type="button" onClick={() => openModal(0)} className='ss-absolute-bottom' >
              See All Photos
            </button>}
          {items.length === 0 && <Skeleton width="60%" />}
        </Grid>

        {items.slice(1, 1 + stackedTileCount).map((src, index) => (
          <Grid key={index} className='ss-gallery-tile'>
            <button
              type="button"
              className="ss-image-carousel-open"
              aria-label={`Open photo ${index + 2} of ${galleryLength}`}
              aria-haspopup="dialog"
              onClick={() => openModal(index + 1)}
            >
              <CarouselImage fit="cover" className='ss-gallery-img' src={src} alt={`Location photo ${index + 2}`} />
            </button>
            {index === stackedTileCount - 1 && hiddenCount > 0 &&
              <button type="button" onClick={() => openModal(index + 1)} className='ss-absolute-full'>+{hiddenCount} {hiddenCount === 1 ? 'Image' : 'Images'}</button>}
          </Grid>
        ))}
      </Grid>
      <SecurSpaceModal overlayClassName="ss-modal-center ss-location-photo-overlay" className="ss-location-photo-modal" isOpen={isCarouselOpen} onRequestClose={closeModal} contentLabel="Location photos">
        <div className='ss-gallery-modal-container'>
          <button type="button" onClick={closeModal} className="ss-image-carousel-control ss-location-photo-close" aria-label="Close photo gallery">
            <AiOutlineClose />
          </button>
          {
            isCarouselOpen && carouselLength && <Suspense fallback={<Skeleton variant="rectangular" height="100%" />}>
              <ImageCarousel images={items} variant="modal" autoFocus ariaLabel="Location photos" startIndex={openPhotoIndex} />
            </Suspense>
          }
        </div>
      </SecurSpaceModal>
    </>
  )
}

export default LocationImageGallery;
